import { type FC, useMemo, useRef, useState } from "react";
import { Home } from "lucide-react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { Badge } from "@/shared/components/ui/badge";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { useDocumentLimitPaywall } from "@/shared/hooks/useDocumentLimitPaywall";
import { currencyInput } from "@/shared/utils/currencyInput";
import { sentryUtils } from "@/shared/utils/sentry";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

interface LoanFormValues {
  lenderName: string;
  balance: string;
  monthlyPayment: string;
  statementFile: File | null;
}

interface LoanTypeConfig {
  screenIdPrefix: string;
  heading: string;
  balanceLabel: string;
  uploadLabel: string;
  uploadDocType: string;
  itemType: string;
  defaultItemName: string;
  errorMessage: string;
  sentryContext: string;
}

export const MORTGAGE_CONFIG: LoanTypeConfig = {
  screenIdPrefix: "add_mortgage",
  heading: "Tell us about the mortgage",
  balanceLabel: "Remaining balance",
  uploadLabel: "Mortgage statement",
  uploadDocType: "mortgage_statement",
  itemType: "Mortgages",
  defaultItemName: "Mortgage",
  errorMessage: "Failed to save mortgage. Please try again.",
  sentryContext: "PropertyMortgage submission failed",
};

export const HELOC_CONFIG: LoanTypeConfig = {
  screenIdPrefix: "add_heloc",
  heading: "Tell us about the home equity line of credit (HELOC)",
  balanceLabel: "Outstanding balance",
  uploadLabel: "HELOC statement",
  uploadDocType: "heloc_statement",
  itemType: "Home equity loans",
  defaultItemName: "Home equity loan",
  errorMessage: "Failed to save home equity loan. Please try again.",
  sentryContext: "PropertyHELOC submission failed",
};

interface PropertyLoanFormProps extends FlowCustomComponentProps {
  propertyId: number;
  config: LoanTypeConfig;
}

function parseFormData(raw: string | string[] | undefined): Partial<LoanFormValues> {
  if (!raw || typeof raw !== "string") return {};
  try {
    return JSON.parse(raw) as Partial<LoanFormValues>;
  } catch {
    return {};
  }
}

export const PropertyLoanForm: FC<PropertyLoanFormProps> = ({
  propertyId,
  config,
  answers,
  setAnswer,
  onComplete,
}) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, debts, addItem, updateItem, isAddingItem, isUpdatingItem } =
    useEstateItems(estateId);

  const property = useMemo(() => assets.find((p) => p.id === propertyId), [assets, propertyId]);

  const existingLoan = useMemo(
    () => debts.find((d) => d.linked_item_id === propertyId && d.item_type === config.itemType),
    [debts, propertyId, config.itemType],
  );

  const screenId = `${config.screenIdPrefix}_${propertyId}`;
  const saved = parseFormData(answers[screenId]);
  const initialForm: LoanFormValues = {
    lenderName: saved.lenderName || "",
    balance: saved.balance || "",
    monthlyPayment: saved.monthlyPayment || "",
    statementFile: null,
  };

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    LoanFormValues,
    { lenderName?: string }
  >(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { checkError, paywall } = useDocumentLimitPaywall();

  const syncToAnswers = (updated: LoanFormValues) => {
    setAnswer(
      screenId,
      JSON.stringify({
        lenderName: updated.lenderName,
        balance: updated.balance,
        monthlyPayment: updated.monthlyPayment,
      }),
    );
  };

  const handleFormChange = (updated: LoanFormValues) => {
    updateForm(updated);
    syncToAnswers(updated);
  };

  const balanceInput = currencyInput(form.balance, (val) =>
    handleFormChange({ ...form, balance: val }),
  );
  const monthlyPaymentInput = currencyInput(form.monthlyPayment, (val) =>
    handleFormChange({ ...form, monthlyPayment: val }),
  );

  const handleSubmit = async () => {
    if (isSubmitting || !estateId) {
      return;
    } else if (!form.lenderName.trim()) {
      setFieldErrors({ lenderName: "Please enter a lender name" });
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let statementDocId: number | undefined;

      if (form.statementFile) {
        statementDocId = await uploadDocumentWithAuth(
          form.statementFile,
          estateId,
          config.uploadDocType,
        );
      }

      const itemData = {
        category: "Secured debts",
        classification: "debt" as const,
        item_type: config.itemType,
        item_name: form.lenderName.trim() || config.defaultItemName,
        balance: parseFloat(form.balance) || 0,
        metadata: {
          lender_name: form.lenderName.trim() || undefined,
          monthly_payment: parseFloat(form.monthlyPayment) || undefined,
        },
        source_document_id: statementDocId,
        linked_item_id: propertyId,
      };

      if (existingLoan) {
        await updateItem(existingLoan.id, itemData);
      } else {
        await addItem({ estate_id: estateId, ...itemData });
      }

      onComplete();
    } catch (err) {
      if (!checkError(err)) {
        setError(config.errorMessage);
        sentryUtils.captureError(err, config.sentryContext, {
          estateId: person?.estate_id,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col">
      {property && (
        <div className="mb-6 flex justify-center">
          <Badge variant="outline" className="gap-1.5 border-none bg-white py-1.5 text-secondary">
            <Home className="h-3.5 w-3.5 text-purple-600" aria-hidden="true" />
            {property.item_name}
          </Badge>
        </div>
      )}

      <h2 className="text-center">{config.heading}</h2>
      <p className="mt-2 text-center">
        Add what you know. You can come back to fill in more anytime.
      </p>

      <div className="mt-8">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting || isAddingItem || isUpdatingItem}
          error={error}
          onSkip={onComplete}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label required>Lender name</Label>
              <Input
                value={form.lenderName}
                onChange={(e) => handleFormChange({ ...form, lenderName: e.target.value })}
                placeholder="e.g., Wells Fargo, Chase, Bank of America"
                className={fieldErrors.lenderName ? "border-error" : ""}
              />
              <FieldError message={fieldErrors.lenderName} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>
                  {config.balanceLabel} <span className="text-tertiary">(optional)</span>
                </Label>
                <CurrencyInput
                  value={balanceInput.displayValue}
                  onChange={balanceInput.handleChange}
                  placeholder="0.00"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>
                  Monthly payment <span className="text-tertiary">(optional)</span>
                </Label>
                <CurrencyInput
                  value={monthlyPaymentInput.displayValue}
                  onChange={monthlyPaymentInput.handleChange}
                  placeholder="0.00"
                />
              </div>
            </div>

            <UploadZone
              label={config.uploadLabel}
              labelSuffix="(optional)"
              file={form.statementFile}
              onFileSelect={(file) => updateForm({ ...form, statementFile: file })}
              onFileRemove={() => updateForm({ ...form, statementFile: null })}
            />
          </div>
        </AddItemLayout>
      </div>
      {paywall}
    </div>
  );
};
